import React, { useState, useEffect, useCallback, useContext } from 'react';
import Codigo from './Codigo';
import Load from './Load';
import { Get_type_code_name } from '../Comun';
import ModalEliminacion from '../modal/ModalEliminacion';
import FilterModal from '../modal/FilterCodigoModal';
import { AuthContext } from '../contexts/AuthContext';

interface Code 
{
    id: number;
    code: string;
    response: string;
    type: number;
    id_tag: number;
    number_of_uses: number;
    number_of_uses_used: number;
    id_provider: number;
}

interface Tag 
{
    id: number;
    tag: string;
    valid_time: string;
    type_tag: number;
}

interface Provider 
{
    id: number;
    name: string;
}

interface CodesProps 
{
    eventId: number;
    title: string;
    onBack: () => void;
}

const Codigos: React.FC<CodesProps> = ({ eventId, title, onBack }) => 
{
    const { token } = useContext(AuthContext);
    
    const [codes, setCodes] = useState<Code[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCode, setSelectedCode] = useState<Code | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
    const [isLoadOpen, setIsLoadOpen] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [filters, setFilters] = useState({
        is_root_response: true as boolean,
        response: null as string | null,
        code: null as string | null,
        type: null as number | null,
        id_tag: null as number | null,
        id_provider: null as number | null, 
    });

    //-----------------------------------------------------------

    const fetchCodes = useCallback(async (page: number = 1) => 
    {
        setLoading(true);
        try 
        {
            const queryParams = new URLSearchParams({
                id_event: eventId.toString(),
                page: page.toString(),
                ...(filters.code && { code: filters.code }),
                ...(filters.type !== null && { type: filters.type.toString() }),
                ...(filters.id_tag !== null && { id_tag: filters.id_tag.toString() }),
                ...(filters.id_provider !== null && { id_provider: filters.id_provider.toString() }),
                ...(filters.response && {[filters.is_root_response ? 'root_response' : 'response']: filters.response})
            });

            const api_url = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${api_url}web/search_event_codes_filter?${queryParams.toString()}`, {
                method: 'GET',
                headers: { 'X-AUTH-TOKEN': token || '' },
            });
    
            if (response.ok) 
            {
                const data = await response.json();
                setCodes(data.results);
                setTotalResults(data.total_results);
                setTotalPages(data.total_pages);
                setCurrentPage(page);
            } 
            else 
            {
                throw new Error('Error al cargar códigos.');
            }
        } 
        catch (error) 
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        } 
        finally 
        {
            setLoading(false);
        }
    }, [eventId, filters, token]);
    
    //-----------------------------------------------------------

    const fetchTags = useCallback(async () => 
    {
        try 
        {
            const api_url = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${api_url}web/get_event_tags?id_event=${eventId}`, {
                method: 'GET',
                headers: { 'X-AUTH-TOKEN': token || '' },
            });

            if (response.ok) 
            {
                const data = await response.json();
                setTags(data);
            } 
            else 
            {
                throw new Error('Error al cargar tags.');
            }
        } 
        catch (error) 
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        } 
    }, [eventId, token]);

    //-----------------------------------------------------------    

    const fetchProviders = useCallback(async () => 
    {
        try 
        {
            const api_url = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${api_url}web/get_event_providers_basic_info?id_event=${eventId}`, {
                method: 'GET',
                headers: { 'X-AUTH-TOKEN': token || '' },
            });

            if (response.ok) 
            {
                const data = await response.json();
                setProviders(data);
            } 
            else 
            {
                throw new Error('Error al cargar providers.');
            }
        } 
        catch (error) 
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        } 
    }, [eventId, token]);

    //-----------------------------------------------------------    

    useEffect(() => 
    {
        const loadInitialData = async () => 
        {
            try 
            {
                await fetchTags();
                await fetchProviders();
            } 
            catch (error) 
            {
                setError('Error al cargar datos iniciales.');
            } 
        };

        loadInitialData();
    }, [fetchTags, fetchProviders]); // Solo se ejecuta al montar el componente

    //-----------------------------------------------------------    

    useEffect(() => 
    {
        fetchCodes(currentPage);
    }, [fetchCodes, currentPage, filters]);

    //-----------------------------------------------------------    

    const handleCSVClick = () => 
    {
        setIsLoadOpen(true);
    } 

    //-----------------------------------------------------------    

    const handleCreateClick = () => 
    {
        setSelectedCode({
            id: -1,
            code: '',
            response: '',
            type: -1,
            id_tag: -1,
            number_of_uses: 1,
            number_of_uses_used: 0,
            id_provider: -1
        });
    };

    //-----------------------------------------------------------    

    const handleEditClick = (code: Code) => 
    {
        setSelectedCode(code);
    };

    //-----------------------------------------------------------    

    const handleFormClose = () => 
    {
        setSelectedCode(null);
        fetchCodes(currentPage);
    };

    //-----------------------------------------------------------    

    const handleDeleteAllClick = () => 
    {
        setIsModalOpen(true);        
    };

    //-----------------------------------------------------------    

    const handleConfirmDelete = async () => 
    {
        setIsModalOpen(false);  

        try 
        {
            const formData = new URLSearchParams();
            formData.append('id_event', eventId.toString());

            const api_url = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${api_url}web/delete_all_event_codes`, {
                method: 'POST',
                headers: {
                    'X-AUTH-TOKEN': token || '',
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData.toString()
            });

            if (response.ok) 
            {                
                setCodes([]);                
            } 
            else 
            {
                throw new Error('Error al borrar todos los códigos.');
            }
        } 
        catch (error) 
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        } 
    };

    //-----------------------------------------------------------

    const handleCancelDelete = () => 
    {
        setIsModalOpen(false);
    };

    //-----------------------------------------------------------    

    const handleFilterOpen = () => 
    {
        setIsFilterModalOpen(true);
    };

    //-----------------------------------------------------------    

    const handleApplyFilters = (filters: 
        {
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        id_provider: number | null;}) => 
    {
        setFilters(filters);
        setCurrentPage(1);
    };

    //-----------------------------------------------------------    

    const handleClearFilters = () => 
    {
        setFilters({
            is_root_response: true,
            response: null,
            code: null,
            type: null,
            id_tag: null,
            id_provider: null
        });
        setCurrentPage(1);
    };

    //-----------------------------------------------------------

    const Get_tag_name = (id: number): string => 
    {
        const tag = tags.find(t => t.id === id);
        return tag ? tag.tag : '<desconocido>';
    };

    //-----------------------------------------------------------    

    const Get_provider_name = (id: number): string => 
    {
        const prov = providers.find(p => p.id === id);
        return prov ? prov.name : '<sin proveedor>';
    };

    //-----------------------------------------------------------

    const handlePageChange = (newPage: number) => 
    {
        if (newPage > 0 && newPage <= totalPages) 
        {
            setCurrentPage(newPage);
            //fetchCodes(newPage);
        }
    };  

    //-----------------------------------------------------------    
    
    return (
        <div className="eventos-container">
            <div className="miguitas">Códigos de {title}</div>
            {selectedCode === null ? (
                <div className="content-wrapper">
                    <div className="header">
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <button onClick={onBack} className="create-client-button">
                                            <i className="fas fa-chevron-left"></i> Volver a Eventos
                                        </button>
                                    </td>
                                    <td>
                                        <button
                                            className="filter-button"
                                            onClick={handleFilterOpen}>
                                            <i className="fas fa-filter"></i> Filtro
                                        </button>
                                        <button
                                            className="filter-button"
                                            onClick={handleCSVClick}>
                                            <i className="fas fa-file-csv"></i>CSV
                                        </button>                                          
                                        <button
                                            className="create-event-button"
                                            onClick={handleCreateClick}>
                                            <i className="fas fa-plus-circle"></i> Crear CÓDIGO
                                        </button>                                      
                                        <button
                                            className="delete-button"
                                            onClick={handleDeleteAllClick}>
                                            <i className="fas fa-trash"></i> Borrar BBDD
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="eventos-container">
                        {error && <p className="error-message">{error}</p>}
                        <div className="codes-container">
                            {loading && !error && <p className="loading-message">Obteniendo códigos...</p>}
                            {!loading && !error && codes.length === 0 && <p>No hay códigos para mostrar.</p>}
                            {!loading && !error && codes.length > 0 && (
                                <div>
                                    <table className="eventos-table">
                                        <tbody>
                                            {codes.map(code => (
                                                <tr key={code.id}>
                                                    <td className="evento-codigo">{code.code}</td>
                                                    <td className="evento-autonext">{code.number_of_uses === 1 ? '1 uso' : `${code.number_of_uses} usos`}</td>
                                                    <td className="evento-autonext">{code.number_of_uses_used === 1 ? '1 usado' : `${code.number_of_uses_used} usados`}</td>
                                                    <td className="evento-autonext">
                                                        {Get_type_code_name(code.type)}
                                                    </td>
                                                    <td className="evento-autonext">{Get_tag_name(code.id_tag)}</td>
                                                    
                                                    <td>{code.response ? code.response : '<sin respuesta>'}</td>
                                                    <td className="evento-provider">{Get_provider_name(code.id_provider)}</td>
                                                    <td className="evento-actions">
                                                        <button className="action-button edit-button" onClick={() => handleEditClick(code)}><i className="fas fa-pencil"></i>Editar</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                    {!loading && codes.length > 0 && (
                    <div className="pagination-controls">                        
                        <div className="pagination-buttons">
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1} >
                                <i className="fas fa-caret-left"></i>
                            </button>
                            <span>{currentPage.toLocaleString('es-ES')} / {totalPages.toLocaleString('es-ES')}</span>
                            <button 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages} >
                                <i className="fas fa-caret-right"></i>
                            </button>
                        </div>
                        <div className="pagination-info">
                            <span>{totalResults.toLocaleString('es-ES')} resultados</span>
                        </div>
                    </div>
                    )}
                </div>
            ) : (
                <div className="form-overlay">
                    <Codigo
                        code={selectedCode}
                        id_event={eventId}
                        tags={tags}
                        onSave={handleFormClose}
                        onCancel={handleFormClose}
                        onDelete={(codeId) => {
                            setCodes(codes.filter(code => code.id !== codeId));
                            handleFormClose();
                        }}
                    />
                </div>
            )}
    
            {isModalOpen && (
                <ModalEliminacion
                    isOpen={isModalOpen}
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                >
                    <p>¿Estás seguro de que quieres eliminar todos los códigos?</p>
                </ModalEliminacion>
            )}
    
            {isFilterModalOpen && (
                <FilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters} // Asegura que el modal reciba la función para limpiar filtros
                    tags={tags}
                    providers={providers}
                    currentFilters={filters} // Pasar los filtros actuales
                />
            )}

            {isLoadOpen && (
                <div className="form-overlay">
                    <Load
                        eventId={eventId}
                        title={title}
                        onSave={() => setIsLoadOpen(false)}
                        onCancel={() => setIsLoadOpen(false)}
                    />
                </div>
            )}
        </div>
    );
    
};

export default Codigos;
